// A session dies after a stretch with no user activity (its idle deadline);
// this hook is how the frontend reports activity so an active user's session
// stays alive. Activity is physical input aimed at the page, never a request:
// Pusher invalidations and background-task progress refetch with nobody at the
// keyboard, and counting those would keep an abandoned tab alive. `mousemove`
// is excluded so a bumped desk or a mouse jiggler is not activity, and focus
// alone is excluded because returning to a tab is not using it.
//
// The frontend only reports that input happened: at once for the first input
// after a quiet interval, so a tab closed soon after use has still reported
// it, then at most once per `REFRESH_INTERVAL_MS`. The server decides whether
// the idle deadline is near enough to move, against its own clock. A failed
// report is dropped; the next input after the interval reports again.
import {useEffect} from "react";
import {refreshSession} from "../api/auth";

// A dev `.env` can override this; see `.env.template`.
export const REFRESH_INTERVAL_MS = Number(import.meta.env.VITE_SESSION_REFRESH_INTERVAL_MS) || 15 * 60 * 1000;

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

export function watchSessionActivity(refresh: () => Promise<unknown>): () => void {
  let throttle: ReturnType<typeof setTimeout> | undefined;
  let inputWhileThrottled = false;
  const report = () => {
    refresh().catch(() => undefined);
    throttle = setTimeout(() => {
      throttle = undefined;
      if (inputWhileThrottled) {
        inputWhileThrottled = false;
        report();
      }
    }, REFRESH_INTERVAL_MS);
  };
  const onInput = () => {
    if (throttle === undefined) {
      report();
    } else {
      inputWhileThrottled = true;
    }
  };
  // Capture on `window` sees input before any component can stop it.
  const options = {capture: true, passive: true};
  for (const type of ACTIVITY_EVENTS) {
    window.addEventListener(type, onInput, options);
  }
  return () => {
    clearTimeout(throttle);
    for (const type of ACTIVITY_EVENTS) {
      window.removeEventListener(type, onInput, options);
    }
  };
}

/** Mount once, at the app root: no single frame wraps every authenticated page. */
export function useSessionRefresh() {
  useEffect(() => watchSessionActivity(refreshSession), []);
}
